<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { ButtonVariants } from './button';
import { Primitive } from 'reka-ui';
import { buttonVariants, resolveButtonColor } from './button';
import { composeAriaLabel } from './aria-label';

interface Props extends PrimitiveProps {
  variant?: ButtonVariants['variant'];
  color?: ButtonVariants['color'];
  size?: ButtonVariants['size'];
  shape?: ButtonVariants['shape'];
  padding?: ButtonVariants['padding'];
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  loadingLabel?: string;
  class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
});

// Attrs are forwarded explicitly (v-bind="$attrs") ahead of the aria-* bindings below, so the
// composed aria-label / aria-busy / aria-disabled always win over whatever the consumer passes.
defineOptions({ inheritAttrs: false });

const composedAriaLabel = computed(() =>
  composeAriaLabel(props.loading ?? false, props.loadingLabel ?? '', props.ariaLabel),
);

// The consumer's @click (forwarded via $attrs) is registered before this handler, but at the target
// the DOM invokes capture listeners before bubble listeners regardless of registration order, so this
// guard runs first and stopImmediatePropagation() is enough to swallow the click while loading.
const onClickCapture = (event: MouseEvent) => {
  if (props.loading) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
};
</script>

<template>
  <Primitive
    :as="as"
    :as-child="asChild"
    :disabled="disabled"
    :class="
      cn(
        buttonVariants({ variant, color: resolveButtonColor(variant, color), size, shape, padding }),
        loading &&
          'pointer-events-none relative isolate overflow-hidden opacity-70 before:absolute before:inset-0 before:-z-10 before:origin-left before:animate-loading-fill before:bg-current-tint motion-reduce:before:animate-none',
        props.class,
      )
    "
    v-bind="$attrs"
    :aria-disabled="disabled || loading || undefined"
    :aria-busy="loading || undefined"
    :aria-label="composedAriaLabel"
    @click.capture="onClickCapture"
  >
    <UiSpinner
      v-if="loading && size === 'icon'"
      aria-hidden="true"
    />
    <slot v-else />
  </Primitive>
</template>
